/**
 * Tab state storage utilities
 * Follows security patterns from 10-security-error-handling.instructions.md
 */

interface TabState {
  isDisabled: boolean;
  isCompleted: boolean;
  timestamp: number;
}

interface TabStates {
  [tabKey: string]: TabState | string | boolean | undefined;
  version: string;
  financesSkipped?: boolean;
}

const TAB_STATE_KEY = "npc_tab_states";
const TAB_STATE_VERSION = "1.0.0";

export const tabStateStorage = {
  /**
   * Get all tab states from localStorage
   */
  getTabStates(): TabStates | null {
    try {
      const stored = localStorage.getItem(TAB_STATE_KEY);
      if (!stored) return null;

      const parsed = JSON.parse(stored) as TabStates;

      // Validate version
      if (parsed.version !== TAB_STATE_VERSION) {
        console.warn("Tab state version mismatch, clearing storage");
        this.clearTabStates();
        return null;
      }

      return parsed;
    } catch (error) {
      console.error("Error reading tab states:", error);
      return null;
    }
  },

  /**
   * Get specific tab state
   */
  getTabState(tabKey: string): TabState | null {
    try {
      const states = this.getTabStates();
      const state = states?.[tabKey];
      return state &&
        typeof state !== "string" &&
        typeof state !== "boolean" &&
        typeof state !== "undefined"
        ? state
        : null;
    } catch (error) {
      console.error("Error reading tab state:", error);
      return null;
    }
  },

  /**
   * Set specific tab state
   */
  setTabState(tabKey: string, state: Partial<TabState>): void {
    try {
      const states: any = this.getTabStates() || { version: TAB_STATE_VERSION };

      states[tabKey] = {
        isDisabled: state.isDisabled ?? false,
        isCompleted: state.isCompleted ?? false,
        timestamp: Date.now(),
      };

      localStorage.setItem(TAB_STATE_KEY, JSON.stringify(states));
    } catch (error) {
      console.error("Error saving tab state:", error);
    }
  },

  /**
   * Update multiple tab states at once
   */
  setTabStates(updates: Record<string, Partial<TabState>>): void {
    try {
      const states: any = this.getTabStates() || { version: TAB_STATE_VERSION };

      Object.entries(updates).forEach(([tabKey, update]) => {
        const existing = states[tabKey];
        const existingState =
          existing && typeof existing !== "string" ? existing : undefined;
        states[tabKey] = {
          isDisabled: update.isDisabled ?? existingState?.isDisabled ?? false,
          isCompleted:
            update.isCompleted ?? existingState?.isCompleted ?? false,
          timestamp: Date.now(),
        };
      });

      localStorage.setItem(TAB_STATE_KEY, JSON.stringify(states));
    } catch (error) {
      console.error("Error saving tab states:", error);
    }
  },

  /**
   * Set finances skipped status
   */
  setFinancesSkipped(skipped: boolean): void {
    try {
      const states: TabStates = this.getTabStates() || { version: TAB_STATE_VERSION };
      states.financesSkipped = skipped;
      localStorage.setItem(TAB_STATE_KEY, JSON.stringify(states));
    } catch (error) {
      console.error("Error saving finances skipped status:", error);
    }
  },

  /**
   * Get finances skipped status
   */
  getFinancesSkipped(): boolean {
    try {
      const states = this.getTabStates();
      return states?.financesSkipped ?? false;
    } catch (error) {
      console.error("Error reading finances skipped status:", error);
      return false;
    }
  },

  /**
   * Clear all tab states
   */
  clearTabStates(): void {
    try {
      localStorage.removeItem(TAB_STATE_KEY);
    } catch (error) {
      console.error("Error clearing tab states:", error);
    }
  },

  /**
   * Clear specific tab state
   */
  clearTabState(tabKey: string): void {
    try {
      const states = this.getTabStates();
      if (states && states[tabKey]) {
        delete states[tabKey];
        localStorage.setItem(TAB_STATE_KEY, JSON.stringify(states));
      }
    } catch (error) {
      console.error("Error clearing tab state:", error);
    }
  },
};
